import type { Area, Category } from '@ilteducation/content-ref-data-types';

export type CategoryKind = 'subject' | 'grade' | 'shortcut';

const CATEGORY_KIND_ICONS: Record<string, string> = {
  subject: 'mdi-shape-outline',
  grade: 'mdi-school-outline',
  shortcut: 'mdi-lightning-bolt-outline',
};

/**
 * Icon for a Category.kind, so a list holding more than one kind stays readable. Areas have
 * no kind, so they get no icon. Unknown kinds fall back to a generic tag rather than vanish.
 */
export const categoryKindIcon = (kind?: string): string =>
  (kind && CATEGORY_KIND_ICONS[kind]) || 'mdi-tag-outline';

/**
 * Manabua content is identified by its immigrant-* subscription types — the repo-wide
 * convention, see resources/areas/Form.vue, categories/Form.vue and videos/Form.vue.
 */
export const isManabuaSubscription = (subscriptionTypes?: string[]): boolean =>
  subscriptionTypes?.some((type) => type.includes('immigrant')) ?? false;

export type CategoryFilter = {
  kind?: CategoryKind;
  excludeKinds?: CategoryKind[];
  /**
   * Markets of the content being edited. A material form edits several language siblings at
   * once, so this is the union of their realms - one shared value is enough to match.
   */
  realms?: string[];
  /** Subscriptions of the content being edited. A single shared value is enough to match. */
  subscriptionTypes?: string[];
  /** Already-selected ids, always kept so an edit can never silently drop them. */
  selectedIds?: string[];
};

/**
 * Categories offered for a piece of content.
 *
 * Matching mirrors books/Form.vue: a category qualifies when it shares *any* subscription
 * type with the content — the two sets do not have to line up, so content spanning
 * polyglutt-* and polylino-* is offered the categories of both.
 */
export const filterCategories = (categories: Category[], filter: CategoryFilter = {}): Category[] => {
  const kept = applyCategoryFilter(categories, filter);
  if (categories.length && !kept.length) {
    // Which field emptied the list is impossible to guess from the UI, so say it out loud.
    const survives = (f: CategoryFilter) => applyCategoryFilter(categories, f).length;
    console.debug('[filterCategories] all %d categories excluded', categories.length, {
      filter,
      wouldSurvive: {
        kindOnly: survives({ kind: filter.kind }),
        realmsOnly: survives({ realms: filter.realms }),
        subscriptionsOnly: survives({ subscriptionTypes: filter.subscriptionTypes }),
      },
      // The ones that pass `kind` are the interesting set — this shows why they then fail.
      passingKind: applyCategoryFilter(categories, { kind: filter.kind })
        .slice(0, 10)
        .map((c) => ({ category: c.category, realms: c.realms, subscriptionTypes: c.subscriptionTypes })),
    });

    // An empty picker is worse than a slightly wide one: a curator simply cannot work. Widen
    // progressively, softest signal first, and say which narrowing was given up.
    const widenings: Array<[string, CategoryFilter]> = [
      ['subscription types', { ...filter, subscriptionTypes: undefined }],
      ['subscription types and realms', { ...filter, subscriptionTypes: undefined, realms: undefined }],
    ];
    for (const [dropped, widened] of widenings) {
      const result = applyCategoryFilter(categories, widened);
      if (result.length) {
        console.debug('[filterCategories] ignoring %s to offer %d categories', dropped, result.length);
        return result;
      }
    }
  }
  return kept;
};

const applyCategoryFilter = (categories: Category[], filter: CategoryFilter): Category[] =>
  categories.filter((category) => {
    if (filter.kind && category.kind !== filter.kind) {
      return false;
    }
    if (category.kind && filter.excludeKinds?.includes(category.kind as CategoryKind)) {
      return false;
    }
    // Whatever is already selected stays visible even if it no longer qualifies, otherwise
    // opening the form would quietly discard it on the next save.
    if (filter.selectedIds?.includes(category.id)) {
      return true;
    }
    // Only exclude on positive evidence: a category with no realms set applies everywhere.
    // Must not short-circuit to true — the subscription check below still has to run.
    if (
      filter.realms?.length &&
      category.realms?.length &&
      !category.realms.some((realm) => filter.realms?.includes(realm))
    ) {
      return false;
    }
    if (filter.subscriptionTypes?.length && category.subscriptionTypes?.length) {
      return category.subscriptionTypes.some((type) => filter.subscriptionTypes?.includes(type));
    }
    return true;
  });

/**
 * Areas have no realm or subscription of their own (see NewArea in content-ref-data-types) —
 * they inherit both from their parent category, so filter them through that.
 */
export const areasOfCategories = (areas: Area[], categories: Category[], selectedIds: string[] = []): Area[] => {
  const categoryIds = new Set(categories.map((category) => category.id));
  return areas.filter((area) => selectedIds.includes(area.id) || categoryIds.has(area.categoryId));
};

/**
 * Shape RefDataAutocomplete works with. Deliberately loose: Category and Area are
 * intersection types, so they get no implicit index signature and a structural item type
 * would reject them.
 */
export type RefDataItem = Record<string, any>;

/**
 * Colour lookup keyed on Category.id (the nanoid).
 *
 * Callers pass either a category's own `id` or an area's `categoryId` — the area's parent
 * category, also a nanoid. Note Category *also* has a `categoryId`, an unrelated legacy
 * number we never use; the runtime string check keeps one from being mistaken for the other.
 */
export const categoryColorLookup = (categories: Category[]) => {
  const colors = categories.reduce((acc: Record<string, string | undefined>, category) => {
    acc[category.id] = category.color;
    return acc;
  }, {});
  return (id?: unknown): string | undefined => (typeof id === 'string' ? colors[id] : undefined);
};
